import { useEffect, useState } from "react";
import { i18n, useTranslation } from "../i18n";
import { relativeTime } from "../lib/format";
import { useAnnouncements } from "../lib/queries";
import type { Announcement, AnnouncementKind } from "../lib/types";
import { Icon } from "./icons";
import { cx } from "./ui";

/*
 * What the platform is telling this customer right now.
 *
 * Sits above everything else on the page rather than inside it, because the
 * thing it says — deploys are paused, this server is being restarted — changes
 * how to read every number below it. A notice filed in a corner of the overview
 * is a notice read after the support ticket has been written.
 *
 * Renders nothing at all when there is nothing to say, which is the normal case
 * and the reason it can be unconditional in the layout. No skeleton, no
 * reserved space: a placeholder for an announcement would be a permanent
 * one-line reminder that announcements exist.
 *
 * The row is built out of the same three parts every status surface in this
 * dashboard uses — a coloured dot, a `micro` label, and the fact — rather than
 * out of banner-specific furniture. That is deliberate: a customer already knows
 * how to read a dot and a small-caps label from the apps table, so a notice
 * arriving in that vocabulary needs no learning.
 */

/**
 * Where a dismissal is remembered.
 *
 * `sessionStorage`, not `localStorage`, and the difference is the point. A
 * maintenance notice dismissed on Monday should come back in a new tab on
 * Tuesday — the platform is still going to be down. Scoping it to the tab means
 * closing a banner clears it from the screen without clearing it from the
 * customer's future, and nothing has to expire or be cleaned up.
 */
const DISMISSED_KEY = "maxlayer.announcements.dismissed";

/**
 * Keyed on the id *and* the revision, so an edited notice reappears.
 *
 * Without the revision, an operator correcting "back at 2pm" to "back at 4pm"
 * would have corrected it only for people who had not yet read the wrong one.
 */
export const dismissalKey = (announcement: Announcement) =>
  `${announcement.id}:${announcement.revision}`;

/**
 * Which notices are still on the screen, given what has been closed.
 *
 * Pure and exported so the two rules that are easy to get backwards can be
 * tested without rendering anything: an edited notice comes back, and a
 * non-dismissible one was never gone.
 */
export function visibleAnnouncements(
  announcements: readonly Announcement[],
  dismissed: ReadonlySet<string>,
): Announcement[] {
  return announcements.filter(
    // A non-dismissible notice ignores the stored set entirely rather than
    // being filtered by it — an operator turning dismissal off for an incident
    // that has got worse must reach the people who closed it while it was news.
    (announcement) => !announcement.dismissible || !dismissed.has(dismissalKey(announcement)),
  );
}

function readDismissed(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [],
    );
  } catch {
    // Private browsing, a quota, or a hand-edited value. A banner that cannot
    // remember a dismissal is worth far more than one that throws on render.
    return new Set();
  }
}

interface Tone {
  /** The bar's own tint and hairline. */
  surface: string;
  /** The dot and the label, which are the only saturated things in the row. */
  mark: string;
  /**
   * Whether the dot pulses. Only the unsettled state does, matching
   * `dot-status-pulse` everywhere else: a scheduled window is not *doing*
   * anything, and a pulse spent on it is a pulse that stops meaning "now".
   */
  pulse?: boolean;
  /**
   * A filled bar rather than a tinted one, which also flips the text off the
   * `fg` tokens — they are unreadable on a saturated ground.
   *
   * Only a promotion gets this. The three states are tints because a state is
   * something to notice while reading the page; an offer is the thing you are
   * meant to act on, and it is the one kind that has earned being loud.
   */
  solid?: boolean;
}

const TONES: Record<AnnouncementKind, Tone> = {
  /*
   * The three state tokens used for exactly what they mean — `accent` is
   * navigable news, `warn` is heading for trouble, `danger` has failed — plus a
   * promotion, which is not a state.
   *
   * A promotion shares accent's hue rather than getting a colour of its own,
   * and that is the design system holding rather than running out of ideas:
   * `mint` means live, and nothing decorative may borrow one of the four state
   * colours; `iris` is scoped to the AI surfaces as a territory. What separates
   * a promotion from a piece of news is weight, not hue.
   */
  info: {
    surface: "border-accent/25 bg-accent/[0.07]",
    mark: "text-accent",
  },
  maintenance: {
    surface: "border-warn/25 bg-warn/[0.07]",
    mark: "text-warn",
  },
  incident: {
    surface: "border-danger/25 bg-danger/[0.07]",
    mark: "text-danger",
    pulse: true,
  },
  /*
   * Dark ink on the accent, not white, and this was a real accessibility bug
   * rather than a preference. White on `#5b9bff` measures **2.77:1** — WCAG AA
   * wants 4.5:1 for text this size, so the loudest bar on the platform was the
   * one hardest to read. The same ink the design system already puts on a solid
   * block measures 7.25:1.
   *
   * `on-inverse` rather than a new `on-accent` token: the pair exists for
   * exactly this job — the ink that goes on a filled surface — and it resolves
   * to the same value in both dashboards, so one component does not get a token
   * of its own.
   */
  promo: {
    surface: "border-accent bg-accent text-on-inverse",
    mark: "text-on-inverse",
    solid: true,
  },
};

/**
 * When it starts, or when it ends, in the words a person would use.
 *
 * Returns null when there is nothing worth saying, which is most of the time —
 * a live notice with no end date has no timing to report, and printing "started
 * 3 days ago" on it would be filling the space rather than using it.
 *
 * Exported because the choice of *which* of the two to show is the logic here,
 * and it is easy to get backwards: before it starts the interesting fact is the
 * start, and after it starts the interesting fact is the end.
 */
export function timingLabel(
  announcement: Pick<Announcement, "startsAt" | "endsAt">,
  now: number = Date.now(),
): string | null {
  const start = new Date(announcement.startsAt).getTime();
  if (Number.isFinite(start) && start > now) {
    return i18n.t("starts", { ns: "announcements", when: relativeTime(announcement.startsAt) });
  }

  if (!announcement.endsAt) return null;
  const end = new Date(announcement.endsAt).getTime();
  if (!Number.isFinite(end)) return null;
  // Past its end and still on screen means the poll has not caught up yet.
  // Saying "ended 2 minutes ago" is more honest than a stale "ends in".
  return end > now
    ? i18n.t("ends", { ns: "announcements", when: relativeTime(announcement.endsAt) })
    : i18n.t("endingNow", { ns: "announcements" });
}

export function AnnouncementBanner({ orgId }: { orgId: string | undefined }) {
  const { data } = useAnnouncements(orgId);
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation("announcements");

  // Re-read once on mount so a second tab opened later starts from whatever
  // this tab's session already holds, rather than from an empty set.
  useEffect(() => setDismissed(readDismissed()), []);

  function dismiss(announcement: Announcement) {
    const next = new Set(dismissed).add(dismissalKey(announcement));
    setDismissed(next);
    try {
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...next]));
    } catch {
      // Closed for this render either way; only the memory of it is lost.
    }
  }

  const visible = visibleAnnouncements(data ?? [], dismissed);

  if (visible.length === 0) return null;

  /*
   * One bar, with the rest folded behind a count.
   *
   * Three stacked bars is 120px of chrome above the page on a laptop and most of
   * a phone screen, which is how a banner stops being read and starts being
   * scrolled past. The API already orders by severity, so the one on top is the
   * one that matters — and the count says plainly that there is more, which a
   * silent truncation would not.
   */
  const [lead, ...rest] = visible;
  const shown = expanded ? visible : lead ? [lead] : [];

  return (
    /*
     * `polite`, not `assertive`. This arrives on a five-minute poll while
     * somebody may be mid-sentence in a form field, and an assertive region
     * interrupts a screen reader to read it out. The banner is important; it is
     * not more important than what the customer is currently typing.
     */
    <div aria-live="polite" className="animate-dropdown-in">
      {shown.map((announcement) => (
        <Row key={announcement.id} announcement={announcement} onDismiss={dismiss} />
      ))}

      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1.5 border-b border-line bg-surface px-4 py-1.5 text-[11px] font-medium text-fg-subtle transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg-muted"
        >
          {expanded ? t("showLess") : t("more", { count: rest.length })}
          <Icon
            name="chevron"
            className={cx("size-3 transition-transform duration-200 ease-smooth", expanded ? "-rotate-90" : "rotate-90")}
          />
        </button>
      )}
    </div>
  );
}

function Row({
  announcement,
  onDismiss,
}: {
  announcement: Announcement;
  onDismiss: (announcement: Announcement) => void;
}) {
  const tone = TONES[announcement.kind];
  const { t } = useTranslation("announcements");
  const timing = timingLabel(announcement);

  return (
    <div className={cx("border-b", tone.surface)}>
      {/*
        Aligned to the same `max-w-6xl` column every page uses, so the text
        starts where the page's own headings start. The *bar* still runs full
        width — a notice that stopped at the content column would read as a
        message about the page rather than about the platform.

        `items-baseline` on the wide layout, not `items-center`: the label and
        the title are different sizes, and centring them puts the small-caps
        label optically high against the sentence it introduces.
      */}
      <div className="mx-auto flex max-w-6xl flex-col gap-1.5 px-4 py-2.5 sm:flex-row sm:items-baseline sm:gap-3 sm:px-6 lg:px-8">
        {/* The dot and the label travel together and never wrap apart. */}
        <span className="flex shrink-0 items-center gap-2">
          <span
            className={cx(
              "dot-status translate-y-px",
              tone.mark,
              tone.pulse && "dot-status-pulse",
            )}
            aria-hidden="true"
          />
          <span
            className={cx(
              "micro",
              // `micro` sets `text-fg-subtle`, which is invisible on the solid
              // bar and too quiet against a tint the dot is already colouring.
              tone.solid ? "text-current" : tone.mark,
            )}
          >
            {t(`kinds.${announcement.kind}`)}
          </span>
        </span>

        {/*
          The message. Clamped at two lines rather than truncated at one: the
          body is where "deploys are affected, we are investigating" lives, and
          one line of it is not enough to be worth reading. Beyond two lines it
          is an essay in a banner, and the full text stays in `title`.

          Back to ordinary foreground tokens on a tint — the dot and the label
          carry the severity, and a paragraph of red is harder to read than the
          thing it is warning about. On the solid promo bar there is no readable
          `fg` to go back to, so it inherits the row's own colour.
        */}
        <p
          title={`${announcement.title} — ${announcement.body}`}
          className="min-w-0 flex-1 text-[13px] leading-6 line-clamp-2"
        >
          <span className={cx("font-medium", tone.solid ? "text-current" : "text-fg")}>
            {announcement.title}
          </span>
          <span className={tone.solid ? "text-current/85" : "text-fg-muted"}>
            {" "}
            — {announcement.body}
          </span>
        </p>

        <span className="flex shrink-0 items-center gap-3">
          {timing && (
            /*
             * Real information rather than decoration, and the reason this is
             * worth a column of its own: "ends in 40 minutes" is the answer to
             * the question a maintenance notice actually provokes, and reading
             * it off a UTC timestamp is work the reader should not be doing.
             *
             * Tabular figures so the number does not jitter sideways as the
             * poll refreshes it.
             */
            <span
              className={cx(
                "text-[11px] tabular-nums whitespace-nowrap",
                tone.solid ? "text-current/75" : "text-fg-subtle",
              )}
            >
              {timing}
            </span>
          )}

          {announcement.link && (
            /*
             * `noreferrer` alongside `noopener`: the target is a URL an operator
             * typed, so it is trusted, but it is also usually a third-party
             * status page and there is no reason to hand it this dashboard's URL
             * as a referrer.
             *
             * An arrow rather than the external-link glyph this used to carry.
             * The arrow says "there is more this way", which is what a status
             * page is; the box-and-arrow says "this leaves the app", which is
             * true of every link in the row and worth saying about none of them.
             */
            <a
              href={announcement.link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cx(
                "group inline-flex items-center gap-1 text-[13px] font-medium whitespace-nowrap",
                tone.solid
                  ? "text-current underline underline-offset-2 hover:no-underline"
                  : "text-accent hover:underline",
              )}
            >
              {announcement.link.label}
              <span
                aria-hidden="true"
                className="transition-transform duration-200 ease-smooth group-hover:translate-x-0.5"
              >
                →
              </span>
            </a>
          )}

          {announcement.dismissible && (
            <button
              type="button"
              onClick={() => onDismiss(announcement)}
              /* 44px under a thumb, per the small-screens rule — this control
                 sits alone at the end of its row, so there is no neighbour to
                 steal a tap from and no density to protect. */
              className={cx(
                "-my-1 -mr-2 grid size-8 shrink-0 place-items-center rounded-md transition-colors duration-200 ease-smooth max-sm:ml-auto pointer-coarse:size-11",
                tone.solid
                  ? "text-current/75 hover:bg-on-inverse/12 hover:text-current"
                  : "text-fg-subtle hover:bg-fg/8 hover:text-fg",
              )}
            >
              <span className="sr-only">{t("dismiss")}</span>
              <Icon name="close" className="size-3.5" />
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
